import type { MatchedDatapoint } from 'features/timeSeries'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import type { NodeOverlay, PageGraphSource } from './canvasTypes'
import type { GraphNode } from './graphTypes'
import { useIsolationController } from './IsolationControllerContext'

export type SelectedGraphNode = {
  node: GraphNode
  datapoints: MatchedDatapoint[]
}

type GraphNodeSelectionState = {
  selectedNodeId: string | null
  selectNodeId: (nodeId: string) => void
  publish: (selection: SelectedGraphNode | null) => void
  selection: SelectedGraphNode | null
  clear: () => void
}

const GraphNodeSelectionContext = createContext<GraphNodeSelectionState | null>(
  null
)
const noDatapoints: MatchedDatapoint[] = []

export const GraphNodeSelectionProvider: React.FC<{
  initialNodeId?: string
  children: React.ReactNode
}> = ({ initialNodeId, children }) => {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(
    initialNodeId ?? null
  )
  const [selection, setSelection] = useState<SelectedGraphNode | null>(null)
  const clear = useCallback((): void => {
    setSelectedNodeId(null)
    setSelection(null)
  }, [])
  const value = useMemo(
    (): GraphNodeSelectionState => ({
      selectedNodeId,
      selectNodeId: setSelectedNodeId,
      publish: setSelection,
      selection,
      clear,
    }),
    [selectedNodeId, selection, clear]
  )
  return (
    <GraphNodeSelectionContext.Provider value={value}>
      {children}
    </GraphNodeSelectionContext.Provider>
  )
}

function useGraphNodeSelectionState(): GraphNodeSelectionState {
  const context = useContext(GraphNodeSelectionContext)
  if (!context) {
    throw new Error(
      'Graph node selection must be used within a GraphNodeSelectionProvider'
    )
  }
  return context
}

export function useGraphNodeSelection(): {
  selection: SelectedGraphNode | null
  clear: () => void
} {
  const { selection, clear } = useGraphNodeSelectionState()
  return { selection, clear }
}

function useClearOnIsolationStart(clear: () => void): void {
  const isolating = useIsolationController() !== undefined
  const wasIsolatingRef = useRef(isolating)
  useEffect(() => {
    if (isolating && !wasIsolatingRef.current) clear()
    wasIsolatingRef.current = isolating
  }, [isolating, clear])
}

export function usePublishGraphNodeSelection(
  pageGraphs: PageGraphSource[],
  datapointsByNodeId: Map<string, MatchedDatapoint[]>
): {
  selectedNodeId: string | undefined
  selectNode: (overlay: NodeOverlay) => void
} {
  const { selectedNodeId, selectNodeId, publish, clear } =
    useGraphNodeSelectionState()
  useClearOnIsolationStart(clear)
  const node = useMemo(
    () =>
      selectedNodeId === null
        ? undefined
        : pageGraphs
            .map(({ graph }) => graph.getNode(selectedNodeId))
            .find(Boolean),
    [pageGraphs, selectedNodeId]
  )
  const datapoints = node ? datapointsByNodeId.get(node.id) : undefined
  const selection = useMemo(
    (): SelectedGraphNode | null =>
      node ? { node, datapoints: datapoints ?? noDatapoints } : null,
    [node, datapoints]
  )
  useEffect(() => {
    publish(selection)
  }, [publish, selection])
  useEffect(
    () => (): void => {
      publish(null)
    },
    [publish]
  )
  const selectNode = useCallback(
    ({ node: selected }: NodeOverlay): void => selectNodeId(selected.id),
    [selectNodeId]
  )
  return { selectedNodeId: node?.id, selectNode }
}
